import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Layers,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  Monitor,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button, Text } from "@core/index";
import {
  useGetVisualMatchingList,
  useGetVisualMatching,
} from "@packages/api/madlenAPI";
import { VisualMatchingResponseDtoStatus } from "@packages/api/madlenAPI.schemas";
import VisualMatchingSmartboardView from "./visual-matching-smartboard-view";
import bannerImageUrl from "@packages/assets/banners/visualmatch.png";

const VisualMatchingView = () => {
  const { t } = useTranslation();
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(
    null,
  );
  const [isSmartboardOpen, setIsSmartboardOpen] = useState(false);

  // Fetch list of visual matching activities
  const {
    data: listResponse,
    isLoading,
    error,
  } = useGetVisualMatchingList({
    page: 1,
    size: 30, // Increased from default 10 to 50
  });
  const matchingActivities = listResponse?.data ?? [];

  // Fetch selected activity data for smartboard
  const { data: selectedActivityData } = useGetVisualMatching(
    selectedActivityId!,
    {
      query: { enabled: !!selectedActivityId },
    },
  );

  const handleOpenSmartboard = (activityId: number) => {
    setSelectedActivityId(activityId);
    setIsSmartboardOpen(true);
  };

  const getStatusIcon = (status: VisualMatchingResponseDtoStatus) => {
    switch (status) {
      case VisualMatchingResponseDtoStatus.READY:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case VisualMatchingResponseDtoStatus.PROCESSING:
        return <Clock className="w-4 h-4 text-amber-500" />;
      case VisualMatchingResponseDtoStatus.FAILED:
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <div className="w-4 h-4 bg-gray-300 rounded-full" />;
    }
  };

  const getStatusColor = (status: VisualMatchingResponseDtoStatus) => {
    switch (status) {
      case VisualMatchingResponseDtoStatus.READY:
        return "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800";
      case VisualMatchingResponseDtoStatus.PROCESSING:
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800";
      case VisualMatchingResponseDtoStatus.FAILED:
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700";
    }
  };

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header with title and create button */}
        <div className="mb-6 sm:mb-8 lg:mb-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 dark:text-white"
            >
              {t("visualMatching.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-2 text-sm sm:text-base"
            >
              {t("visualMatching.description")}
            </Text>
          </div>
          <div className="flex-shrink-0">
            <Link to="/content/visual-matching/create">
              <Button
                size="lg"
                rounded="xl"
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <span className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  {t("visualMatching.createButton")}
                </span>
              </Button>
            </Link>
          </div>
        </div>

        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="relative overflow-hidden rounded-2xl border border-dark-200 dark:border-dark-400 bg-white dark:bg-dark-600">
            {/* Gradient background overlay */}
            <div
              className="absolute inset-0 pointer-events-none z-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0, 102, 255, 0.10) 0%, rgba(0, 0, 0, 0.85) 100%)",
              }}
            />
            <div className="flex flex-col md:flex-row h-full p-4 items-center relative z-10">
              {/* Left image */}
              <div className="md:w-2/6 w-full h-28 relative flex items-center justify-start">
                <img
                  src={bannerImageUrl}
                  alt=""
                  className="w-full h-full object-contain mb-2"
                  style={{ position: "static" }}
                />
              </div>
              {/* Right text */}
              <div className="flex-1 p-5 sm:p-6 lg:p-8 flex items-end justify-end">
                <div>
                  <Text
                    variant="p"
                    className="text-gray-700 dark:text-gray-200 text-sm sm:text-base lg:text-lg leading-relaxed"
                  >
                    Improve learning with <b>visual matching</b>
                    <br />
                    Match words to pictures for faster vocabulary growth!
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content card */}
        <div className="">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t("common.loading")}...
              </Text>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <Text variant="p" className="text-red-500">
                {t("common.error")}
              </Text>
            </div>
          ) : matchingActivities.length === 0 ? (
            <div className="text-center py-20">
              <div className="mx-auto w-24 h-24 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Layers className="w-12 h-12 text-amber-600 dark:text-amber-400" />
              </div>
              <Text variant="h2" className="mb-3 text-gray-900 dark:text-white">
                {t("visualMatching.empty.title")}
              </Text>
              <Text
                variant="p"
                status="secondary"
                className="mb-8 max-w-md mx-auto text-gray-600 dark:text-gray-400"
              >
                {t("visualMatching.empty.description")}
              </Text>
              <Link to="/content/visual-matching/create">
                <Button
                  size="lg"
                  rounded="xl"
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  <span className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    {t("visualMatching.empty.createButton")}
                  </span>
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-4">
              {matchingActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 transform hover:-translate-y-1"
                >
                  {/* Card Header */}
                  <div className="p-6 pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-xl flex items-center justify-center shadow-sm">
                          <Eye className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg truncate">
                            {activity.title ||
                              t("visualMatching.preview.titlePlaceholder")}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {activity.questions?.length ?? 0}{" "}
                            {t("visualMatching.pairCount")}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6 line-clamp-3">
                      {activity.description ||
                        t("visualMatching.preview.noDescription")}
                    </p>
                  </div>

                  {/* Card Footer */}
                  <div className="px-6 pb-6">
                    <div className="flex justify-end">
                      <div className="flex items-center gap-2">
                        {activity.questions &&
                          activity.questions.length > 0 && (
                            <Button
                              onClick={() => handleOpenSmartboard(activity.id!)}
                              variant="outline"
                              size="sm"
                              rounded="xl"
                              className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-all duration-200 group-hover:border-blue-300 group-hover:bg-blue-50"
                              leftIcon={<Monitor size={14} />}
                            >
                              {t("visualMatching.smartboard.openSmartboard")}
                            </Button>
                          )}
                        <Link to={`/content/visual-matching/${activity.id}`}>
                          <Button
                            variant="solid"
                            size="sm"
                            rounded="xl"
                            className="bg-amber-600 hover:bg-amber-700 text-white border-0 shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            {t("common.view")}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Smart Board Modal */}
        {selectedActivityData && (
          <VisualMatchingSmartboardView
            isOpen={isSmartboardOpen}
            onClose={() => {
              setIsSmartboardOpen(false);
              setSelectedActivityId(null);
            }}
            visualMatchingData={selectedActivityData}
          />
        )}
      </div>
    </div>
  );
};

export default VisualMatchingView;
